import type { ComponentTemplate } from "schemas/generated/componentTemplates";

import { accordionBlock } from "replo-templates/accordionBlock";
import { beforeAfterSlider } from "replo-templates/beforeAfterSlider";
import bulletedList from "replo-templates/bulletedList";
import { bundleBuilder, pdpBundleBuilder } from "replo-templates/bundleBuilder";
import { button } from "replo-templates/button";
import { buyWithPrimeButton } from "replo-templates/buyWithPrimeButton";
import { bannerCarousel } from "replo-templates/carousel/banner";
import { carousel } from "replo-templates/carousel/base";
import { centeredCarousel } from "replo-templates/carousel/centered";
import { carouselIndicators } from "replo-templates/carousel/indicators";
import { carouselNextControl } from "replo-templates/carousel/next";
import { carouselPanels } from "replo-templates/carousel/panels";
import { carouselPreviousControl } from "replo-templates/carousel/previous";
import { carouselSlides } from "replo-templates/carousel/slides";
import { slideshowCarousel } from "replo-templates/carousel/slideshow";
import { carouselWithImageIndicators } from "replo-templates/carousel/withImageIndicators";
import { carouselWithMultipleItems } from "replo-templates/carousel/withMultipleItems";
import { carouselWithProductImages } from "replo-templates/carousel/withProductImages";
import circle from "replo-templates/circle";
import { collapsibleV2 } from "replo-templates/collapsibleV2";
import { countdownTimer } from "replo-templates/countdownTimer";
import { marquee } from "replo-templates/dynamic/marquee";
import { feraMediaGalleryWidget } from "replo-templates/feraMediaGalleryWidget";
import { feraProductRatingWidget } from "replo-templates/feraProductRatingWidget";
import { feraProductReviewsWidget } from "replo-templates/feraProductReviewsWidget";
import { feraStoreReviewsWidget } from "replo-templates/feraStoreReviewsWidget";
import { h1 } from "replo-templates/h1";
import { h2 } from "replo-templates/h2";
import { h3 } from "replo-templates/h3";
import { horizontalRule } from "replo-templates/horizontalRule";
import { customHtml } from "replo-templates/htmlContent";
import { icon } from "replo-templates/icon";
import { image } from "replo-templates/image";
import { infiniteOptionsWidget } from "replo-templates/infiniteOptionsWidget";
import { googleMapsEmbed } from "replo-templates/integrations/googleMapsEmbed";
import { klaviyoEmbed } from "replo-templates/integrations/klaviyoEmbed";
import { dynamicBundle } from "replo-templates/integrations/rebuy/dynamicBundle";
import { homePageCrossSell } from "replo-templates/integrations/rebuy/homePageCrossSell";
import { productAddOns } from "replo-templates/integrations/rebuy/productAddOns";
import { productPageCrossSell } from "replo-templates/integrations/rebuy/productPageCrossSell";
import { productPageUpsell } from "replo-templates/integrations/rebuy/productPageUpsell";
import { tikTokEmbed } from "replo-templates/integrations/tikTokEmbed";
import { vimeoEmbed } from "replo-templates/integrations/vimeoEmbed";
import { youtubeEmbed } from "replo-templates/integrations/youtubeEmbed";
import { judgeProductRatingWidget } from "replo-templates/judgeProductRatingWidget";
import { judgeProductReviewsWidget } from "replo-templates/judgeProductReviewsWidget";
import { junipProductRating } from "replo-templates/junipProductRating";
import { junipReviews } from "replo-templates/junipReviews";
import { kachingBundles } from "replo-templates/kachingBundles";
import { knoCommerceWidget } from "replo-templates/knoCommerceWidget";
import { looxProductRating } from "replo-templates/looxProductRating";
import { looxReviews } from "replo-templates/looxReviews";
import { modal } from "replo-templates/modal";
import { okendoProductRatingSummary } from "replo-templates/okendoProductRatingSummary";
import { okendoReviewsWidget } from "replo-templates/okendoReviewsWidget";
import { postscriptSignupForm } from "replo-templates/postscriptSignupForm";
import { addToCartButton } from "replo-templates/product/addToCartButton";
import buyNow from "replo-templates/product/buyNow";
import compareAtPrice from "replo-templates/product/compareAtPrice";
import description from "replo-templates/product/description";
import { dynamicCheckoutButtons } from "replo-templates/product/dynamicCheckoutButtons";
import { paymentTerms } from "replo-templates/product/paymentTerms";
import price from "replo-templates/product/price";
import product from "replo-templates/product/product";
import { productCollection } from "replo-templates/product/productCollection";
import {
  optionSelect,
  optionSelectDropdown,
  optionSelectWithRadio,
} from "replo-templates/product/productOptionSelect";
import productWithCarousel from "replo-templates/product/productWithCarousel";
import productWithSwatch from "replo-templates/product/productWithSwatch";
import productWithVariantAndOptionList from "replo-templates/product/productWithVariantAndOptionList";
import quantity from "replo-templates/product/quantity";
import { quantitySelector } from "replo-templates/product/quantitySelector";
import {
  sellingPlanDropdown,
  sellingPlanList,
  sellingPlanListWithRadio,
} from "replo-templates/product/sellingPlanList";
import { subscribeAndSave } from "replo-templates/product/subscribeAndSave";
import { swatchOptionListCircular } from "replo-templates/product/swatchOptionCircular";
import title from "replo-templates/product/title";
import variantImage from "replo-templates/product/variantImage";
import {
  variantList,
  variantListWithRadio,
  variantSelectDropdown,
} from "replo-templates/product/variantList";
import { radioButton } from "replo-templates/radioButton";
import { reviewsIoProductRating } from "replo-templates/reviewsIoProductRating";
import { reviewsIoReviews } from "replo-templates/reviewsIoReviews";
import { imageGrid } from "replo-templates/selection/imageGrid";
import { productImageGrid } from "replo-templates/selection/productImageGrid";
import { productQuantitySelection } from "replo-templates/selection/productQuantity";
import { productSelection } from "replo-templates/selection/productSelection";
import { appBlocks } from "replo-templates/shopify/appBlocks";
import { rawLiquid, shopifySection } from "replo-templates/shopify/section";
import { shopifyProductRatingWidget } from "replo-templates/shopifyProductRatingWidget";
import { spinner } from "replo-templates/spinner";
import {
  container,
  fourColumns,
  horizontalStack,
  oneByTwoColumns,
  threeColumns,
  twoByOneColumns,
  twoColumns,
} from "replo-templates/stacks";
import { stampedProductRatingWidget } from "replo-templates/stampedProductRatingWidget";
import { stampedProductReviewsWidget } from "replo-templates/stampedProductReviewsWidget";
import { starRating } from "replo-templates/starRating";
import { starRatingAndText } from "replo-templates/starRatingAndText";
import { staySubscriptionWidget } from "replo-templates/staySubscriptionWidget";
import { switchTemplate } from "replo-templates/switch";
import { tabsV2Left, tabsV2Top } from "replo-templates/tabsV2";
import { text } from "replo-templates/text";
import { tooltip } from "replo-templates/tooltip";
import { videoPlayer } from "replo-templates/videoPlayer";
import { yotpoProductRating } from "replo-templates/yotpoProductRating";
import { yotpoReviews } from "replo-templates/yotpoReviews";

const getHardcodedComponentTemplates = (): ComponentTemplate[] => {
  const hardcodedTemplates = [
    text,
    image,
    horizontalStack,
    container,
    twoColumns,
    oneByTwoColumns,
    twoByOneColumns,
    threeColumns,
    fourColumns,
    horizontalRule,
    beforeAfterSlider,
    product,
    productCollection,
    productWithCarousel,
    productWithSwatch,
    productWithVariantAndOptionList,
    description,
    title,
    quantity,
    variantImage,
    price,
    compareAtPrice,
    buyNow,
    addToCartButton,
    variantList,
    quantitySelector,
    variantSelectDropdown,
    variantListWithRadio,
    optionSelect,
    sellingPlanList,
    sellingPlanDropdown,
    sellingPlanListWithRadio,
    optionSelectDropdown,
    optionSelectWithRadio,
    swatchOptionListCircular,
    googleMapsEmbed,
    klaviyoEmbed,
    videoPlayer,
    vimeoEmbed,
    youtubeEmbed,
    tikTokEmbed,
    button,
    switchTemplate,
    radioButton,
    shopifySection,
    collapsibleV2,
    tabsV2Top,
    tabsV2Left,
    starRating,
    rawLiquid,
    appBlocks,
    marquee,
    customHtml,
    carousel,
    carouselWithMultipleItems,
    centeredCarousel,
    bannerCarousel,
    carouselWithImageIndicators,
    slideshowCarousel,
    carouselWithProductImages,
    carouselSlides,
    carouselNextControl,
    carouselPreviousControl,
    carouselIndicators,
    carouselPanels,
    staySubscriptionWidget,
    knoCommerceWidget,
    okendoReviewsWidget,
    okendoProductRatingSummary,
    h1,
    h2,
    h3,
    bulletedList,
    countdownTimer,
    icon,
    starRatingAndText,
    spinner,
    circle,
    junipReviews,
    junipProductRating,
    yotpoReviews,
    yotpoProductRating,
    looxReviews,
    looxProductRating,
    reviewsIoReviews,
    reviewsIoProductRating,
    dynamicCheckoutButtons,
    paymentTerms,
    accordionBlock,
    buyWithPrimeButton,
    dynamicBundle,
    homePageCrossSell,
    productAddOns,
    productPageCrossSell,
    productPageUpsell,
    modal,
    stampedProductReviewsWidget,
    stampedProductRatingWidget,
    feraProductRatingWidget,
    feraProductReviewsWidget,
    feraStoreReviewsWidget,
    feraMediaGalleryWidget,
    shopifyProductRatingWidget,
    judgeProductRatingWidget,
    judgeProductReviewsWidget,
    infiniteOptionsWidget,
    kachingBundles,
    postscriptSignupForm,
    subscribeAndSave,
    tooltip,
    productQuantitySelection,
    productSelection,
    imageGrid,
    productImageGrid,
    bundleBuilder,
    pdpBundleBuilder,
  ];

  return hardcodedTemplates;
};

export const getTemplateName = (template: ComponentTemplate) => {
  const [categoryOrName, ...rest] = template.name.split("/");
  return template.name.includes("/") ? rest.join("/") : categoryOrName;
};

export const hardcodedComponentTemplates: ComponentTemplate[] =
  getHardcodedComponentTemplates();

export const basicTemplates: ComponentTemplate[] = [
  // Basic Structure Components
  container,
  horizontalStack,
  twoColumns,
  threeColumns,

  // Typography Components
  text,
  h1,
  h2,
  h3,
  title,
  description,

  // Product Components
  product,
  productWithCarousel,
  price,
  compareAtPrice,
  carouselWithProductImages,

  // Interactive Components
  button,
  addToCartButton,
  buyNow,
  quantitySelector,

  // Product Selection Components
  variantList,
  variantSelectDropdown,
  optionSelect,
  swatchOptionListCircular,
  subscribeAndSave,

  // Media Components
  image,
  carousel,
  videoPlayer,

  // Review Components
  starRating,
  starRatingAndText,

  // Additional Features
  accordionBlock,
  modal,
  tooltip,
];
